import { supabase } from '../supabaseClient';

export type CompanyAdminRole = 'admin';
export type CompanyAdminStatus = 'active' | 'disabled';

export interface CompanyAdminProfile {
  id: string;
  company_id: string;
  full_name: string | null;
  email?: string | null;
  role: CompanyAdminRole;
  status?: CompanyAdminStatus | null;
  created_at?: string | null;
  avatar_url?: string | null;
}

export interface CreateCompanyAdminParams {
  email: string;
  fullName: string;
  password: string;
}

interface AdminAccountsResponse {
  profiles?: CompanyAdminProfile[];
  profile?: CompanyAdminProfile;
}

async function getFunctionErrorMessage(error: unknown): Promise<string | null> {
  const context = (error as { context?: Response | null })?.context;
  if (context) {
    try {
      const payload = await context.clone().json() as { error?: string; message?: string };
      return payload.error ?? payload.message ?? null;
    } catch {
      // Fall back to the SDK error below.
    }
  }

  return error instanceof Error ? error.message : null;
}

export const adminService = {
  async fetchCompanyAdmins(): Promise<CompanyAdminProfile[]> {
    const { data, error } = await supabase.functions.invoke('admin-accounts', {
      body: { action: 'list' },
    });

    if (error) {
      throw new Error(await getFunctionErrorMessage(error) ?? 'Failed to load admin accounts');
    }

    return ((data as AdminAccountsResponse | null)?.profiles ?? []);
  },

  async createCompanyAdmin(params: CreateCompanyAdminParams): Promise<CompanyAdminProfile | null> {
    const { data, error } = await supabase.functions.invoke('admin-accounts', {
      body: {
        action: 'create',
        email: params.email,
        fullName: params.fullName,
        password: params.password,
      },
    });

    if (error) {
      throw new Error(await getFunctionErrorMessage(error) ?? 'Failed to create admin account');
    }

    return ((data as AdminAccountsResponse | null)?.profile ?? null);
  },
};
